import React, { createContext, useContext } from "react";
import { getDefaultDialCodeFromUserTimezone } from "repoV2/features/Common/modules/Timezone/Timezone.utils";
import { getExpressInterestButtonText } from "repoV2/features/ExpressInterest/utils/getExpressInterestButtonText";
import { LISTING_STATUSES } from "repoV2/features/Listing/Listing.constants";
import { ICheckoutListingDetails } from "../utils/apis/ListingDetailsApi/IListingDetailsApi.interface";
import { ICheckoutCreatorDetails } from "../utils/apis/CreatorDetailsApi/ICreatorDetailsApi.interface";
import { express_interest_form } from "./ExpressInterestSection/modules/ExpressInterestForm/ExpressInterestForm.constants";

interface IExpressInterestContext {
    defaultCountryCode?: string;
    formTypeName?: string;
    listingExpired?: boolean;
    postInterestUrl?: string;
    expressInterestFormPlaceholders: {
        name: string;
        email: string;
        phone: string;
        message: string;
    };
}

interface IExpressInterestContextProvider {
    listingDetails: ICheckoutListingDetails;
    creatorDetails: ICheckoutCreatorDetails;
    children?: JSX.Element | JSX.Element[];
}

const initialContext: IExpressInterestContext = {
    formTypeName: "",
    listingExpired: false,
    postInterestUrl: "",
    expressInterestFormPlaceholders:
        express_interest_form.basic_details.placeholders,
};

const ExpressInterestContext = createContext(initialContext);

const ExpressInterestContextProvider: React.FC<IExpressInterestContextProvider> =
    props => {
        const { children, listingDetails, creatorDetails } = props;
        const {
            status = LISTING_STATUSES.LIVE,
            metadata: {
                post_interest_url: postInterestUrl = "",
                hide_price: hideListingPrice = false,
            } = {},
        } = listingDetails;
        const {
            custom_get_in_touch_form_field_name = "",
            custom_get_in_touch_form_field_email = "",
            custom_get_in_touch_form_field_phone = "",
            custom_get_in_touch_form_field_message = "",
            custom_get_in_touch_form_cta = "",
        } = creatorDetails;

        const defaultCountryCode = getDefaultDialCodeFromUserTimezone();
        const formTypeName =
            custom_get_in_touch_form_cta ||
            getExpressInterestButtonText({
                listingStatus: status,
                hideListingPrice,
            });

        const {
            basic_details: { placeholders },
        } = express_interest_form;

        const expressInterestFormPlaceholders: IExpressInterestContext["expressInterestFormPlaceholders"] =
            {
                name: custom_get_in_touch_form_field_name || placeholders.name,
                email:
                    custom_get_in_touch_form_field_email || placeholders.email,
                phone:
                    custom_get_in_touch_form_field_phone || placeholders.phone,
                message:
                    custom_get_in_touch_form_field_message ||
                    placeholders.message,
            };

        const listingExpired = status === LISTING_STATUSES.EXPIRED;
        // @dev any initial processing (that does not change after mount)
        // on form specific data will happen here.
        return (
            <ExpressInterestContext.Provider
                value={{
                    defaultCountryCode,
                    formTypeName,
                    listingExpired,
                    postInterestUrl,
                    expressInterestFormPlaceholders,
                }}
            >
                {children}
            </ExpressInterestContext.Provider>
        );
    };

export const useExpressInterestContext = () => {
    return useContext(ExpressInterestContext);
};

export default ExpressInterestContextProvider;
